import { useMemo, useEffect, useState } from "react";
import { supabase } from '../lib/supabase';

// Tipo básico para mídia
interface Media {
  id: string;
  title: string;
  description?: string;
  url: string;
  type: string;
  selected_screens: string[];
  display_time?: number;
  active: boolean;
  organization_id?: string;
}

// Tipo para agendamento
interface MediaSchedule {
  id: string;
  media_id: string;
  status: 'active' | 'inactive' | 'pending' | 'completed';
  start_date?: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  days_of_week?: number[];
  recurrence_pattern?: 'once' | 'daily' | 'weekly' | 'custom';
  timezone?: string;
}

interface UsePlayerMediaFilteringProps {
  medias: Media[];
  selectedScreen: string;
  userId: string;
}

interface UsePlayerMediaFilteringResult {
  filteredMedias: Media[];
  filteredMediasHash: string;
  filteredMediaIds: string[];
}

// Função auxiliar para verificar se um agendamento está ativo no momento
const isScheduleActive = (schedule: MediaSchedule): boolean => {
  if (!schedule?.status || schedule.status !== 'active') {
    console.log('🔍 [SCHEDULE] Agendamento não ativo:', schedule.status);
    return false;
  }

  try {
    const now = new Date();
    const currentDay = now.getDay(); // 0 = domingo, 6 = sábado
    const pad = (n: number) => n.toString().padStart(2, '0');
    const currentDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`; // YYYY-MM-DD (local)
    const currentTime = now.toTimeString().split(' ')[0]; // HH:MM:SS

    console.log('🕒 [SCHEDULE] Verificando agendamento:', {
      scheduleId: schedule.id,
      mediaId: schedule.media_id,
      currentDate,
      currentTime,
      currentDay,
      schedule: {
        start_date: schedule.start_date,
        end_date: schedule.end_date,
        start_time: schedule.start_time,
        end_time: schedule.end_time,
        days_of_week: schedule.days_of_week,
        recurrence_pattern: schedule.recurrence_pattern
      }
    });

    // Verificar intervalo de datas
    if (schedule.start_date && currentDate < schedule.start_date) {
      console.log('❌ [SCHEDULE] Data atual anterior à data de início');
      return false;
    }

    if (schedule.end_date && currentDate > schedule.end_date) {
      console.log('❌ [SCHEDULE] Data atual posterior à data de fim');
      return false;
    }

    // Verificar dias da semana para padrões que usam
    if (schedule.recurrence_pattern === 'weekly' || schedule.recurrence_pattern === 'custom') {
      if (schedule.days_of_week && schedule.days_of_week.length > 0) {
        if (!schedule.days_of_week.includes(currentDay)) {
          console.log('❌ [SCHEDULE] Dia da semana não permitido. Atual:', currentDay, 'Permitidos:', schedule.days_of_week);
          return false;
        }
      }
    }

    // Verificar horários
    if (schedule.start_time && currentTime < schedule.start_time) {
      console.log('❌ [SCHEDULE] Horário atual anterior ao horário de início');
      return false;
    }

    if (schedule.end_time && currentTime > schedule.end_time) {
      console.log('❌ [SCHEDULE] Horário atual posterior ao horário de fim');
      return false;
    }

    console.log('✅ [SCHEDULE] Agendamento ativo!');
    return true;
  } catch (error) {
    console.error('❌ [SCHEDULE] Erro ao verificar agendamento:', error);
    return false;
  }
};

// Buscar agendamentos ativos para uma mídia
const getActiveScheduleForMedia = async (mediaId: string, screenId: string): Promise<MediaSchedule | null> => {
  try {
    console.log('🔍 [SCHEDULE] Buscando agendamentos para mídia:', mediaId, 'screen:', screenId);

    const { data, error } = await supabase
      .from('media_schedules')
      .select('*')
      .eq('media_id', mediaId)
      .eq('screen_id', screenId)
      .eq('status', 'active');

    if (error) {
      console.error('❌ [SCHEDULE] Erro ao buscar agendamentos:', error);
      return null;
    }

    if (!data || data.length === 0) {
      console.log('📋 [SCHEDULE] Nenhum agendamento encontrado para mídia:', mediaId);
      return null;
    }

    console.log('📋 [SCHEDULE] Agendamentos encontrados:', data.length);

    // Verificar qual agendamento está ativo no momento
    for (const schedule of data) {
      if (isScheduleActive(schedule)) {
        console.log('✅ [SCHEDULE] Agendamento ativo encontrado:', schedule.id);
        return schedule;
      }
    }

    console.log('⏰ [SCHEDULE] Nenhum agendamento ativo no momento para mídia:', mediaId);
    return null;
  } catch (error) {
    console.error('❌ [SCHEDULE] Erro ao verificar agendamentos:', error);
    return null;
  }
};

export function usePlayerMediaFiltering({
  medias,
  selectedScreen,
  userId,
}: UsePlayerMediaFilteringProps): UsePlayerMediaFilteringResult {
  const [filteredMedias, setFilteredMedias] = useState<Media[]>([]);

  useEffect(() => {
    const filterMedias = async () => {
      try {
        console.log('🔍 [FILTERING] Iniciando filtragem de mídias com agendamento');
        console.log('🔍 [FILTERING] selectedScreen:', selectedScreen);
        console.log('🔍 [FILTERING] Total de mídias:', medias.length);
        console.log('🔍 [FILTERING] Mídias recebidas:', medias.map(m => ({
          id: m.id,
          title: m.title,
          active: m.active,
          selected_screens: m.selected_screens
        })));

        if (!selectedScreen) {
          // MODIFICAÇÃO: Quando ativado via Companion (sem tela associada),
          // exibir TODAS as mídias ativas do usuário (comportamento global)
          console.log('🌐 [FILTERING] Nenhuma tela selecionada - Modo Companion/Global');
          console.log('🌐 [FILTERING] Exibindo TODAS as mídias ativas do usuário');

          const allActiveMedias = medias.filter(media => media.active);

          console.log('🌐 [FILTERING] Mídias ativas encontradas:', allActiveMedias.length);
          console.log('🌐 [FILTERING] Lista:', allActiveMedias.map(m => ({
            id: m.id,
            title: m.title,
            selected_screens: m.selected_screens
          })));

          setFilteredMedias(allActiveMedias);
          return;
        }

        // Primeira filtragem: mídias ativas e associadas à tela selecionada (com normalização)
        const screenFilteredMedias = medias.filter(media => {
          const isActive = media.active;
          const hasScreens = media.selected_screens && Array.isArray(media.selected_screens);
          const normalizedSelected = hasScreens
            ? media.selected_screens.map((s: any) => (typeof s === 'string' ? s.trim().toLowerCase() : (s?.id || s)?.toString()?.trim()?.toLowerCase()))
            : [];
          const includesScreen = hasScreens && normalizedSelected.includes(selectedScreen.trim().toLowerCase());

          console.log(`🔍 [FILTERING] Mídia ${media.title}:`, {
            isActive,
            hasScreens,
            selected_screens: media.selected_screens,
            normalizedSelected,
            includesScreen,
            selectedScreen
          });

          return isActive && includesScreen;
        });

        // Fallback: se nenhuma mídia foi associada explicitamente à tela, incluir mídias ativas sem telas configuradas (globais)
        let candidateMedias = screenFilteredMedias;
        if (candidateMedias.length === 0) {
          const globalMedias = medias.filter(media => {
            const isActive = media.active;
            const hasScreens = Array.isArray(media.selected_screens);
            const noScreensConfigured = !hasScreens || media.selected_screens.length === 0;
            return isActive && noScreensConfigured;
          });
          console.log('📋 [FILTERING] Fallback para mídias globais sem telas configuradas:', globalMedias.length);
          candidateMedias = globalMedias;
        }

        // Fallback final: se ainda não há candidatas, exibir quaisquer mídias ativas do usuário
        if (candidateMedias.length === 0) {
          const anyActiveMedias = medias.filter(m => m.active);
          console.log('📋 [FILTERING] Fallback final para quaisquer mídias ativas:', anyActiveMedias.length);
          candidateMedias = anyActiveMedias;
        }

        console.log('📋 [FILTERING] Mídias filtradas por tela:', screenFilteredMedias.length);

        // Segunda filtragem: verificar agendamentos
        // IMPORTANTE: Mídias que passaram na filtragem por tela são INCLUÍDAS por padrão
        // Agendamentos são usados para RESTRINGIR exibição a horários específicos
        const scheduledFilteredMedias: Media[] = [];

        for (const media of candidateMedias) {
          console.log(`🕒 [FILTERING] Verificando agendamento para mídia: ${media.title} (${media.id})`);

          // Verificar se existe ALGUM agendamento configurado para esta mídia e tela
          const { data: existingSchedules } = await supabase
            .from('media_schedules')
            .select('*')
            .eq('media_id', media.id)
            .eq('screen_id', selectedScreen);

          // Se não tem nenhum agendamento configurado, incluir normalmente (comportamento padrão)
          if (!existingSchedules || existingSchedules.length === 0) {
            console.log(`✅ [FILTERING] Mídia ${media.title} sem agendamento configurado - incluindo (padrão)`);
            scheduledFilteredMedias.push(media);
            continue;
          }

          // Se tem agendamentos, verificar se algum está ativo agora
          const activeSchedule = await getActiveScheduleForMedia(media.id, selectedScreen);

          if (activeSchedule) {
            console.log(`✅ [FILTERING] Mídia ${media.title} tem agendamento ativo - incluindo`);
            scheduledFilteredMedias.push(media);
          } else {
            // Tem agendamento mas não está ativo - verificar se algum agendamento está com status 'active'
            // Se todos os agendamentos estão inativos/pendentes, ainda assim incluir a mídia
            const hasActiveStatusSchedule = existingSchedules.some((s: any) => s.status === 'active');

            if (hasActiveStatusSchedule) {
              console.log(`⏰ [FILTERING] Mídia ${media.title} tem agendamento ativo mas fora do horário - NÃO incluindo`);
            } else {
              // Nenhum agendamento com status 'active', incluir a mídia normalmente
              console.log(`✅ [FILTERING] Mídia ${media.title} - agendamentos inativos, incluindo normalmente`);
              scheduledFilteredMedias.push(media);
            }
          }
        }

        console.log('✅ [FILTERING] Mídias filtradas final:', scheduledFilteredMedias.length);
        console.log('📋 [FILTERING] Lista final:', scheduledFilteredMedias.map(m => ({
          id: m.id,
          title: m.title
        })));

        setFilteredMedias(scheduledFilteredMedias);
      } catch (error) {
        console.error('❌ [FILTERING] Erro ao filtrar mídias:', error);
        // Em caso de erro, usar filtragem simples sem agendamento
        const fallbackFiltered = medias.filter(media => {
          return media.active &&
            media.selected_screens &&
            Array.isArray(media.selected_screens) &&
            media.selected_screens.includes(selectedScreen);
        });
        setFilteredMedias(fallbackFiltered);
      }
    };

    filterMedias();

    // Atualizar a cada 30 segundos para verificar mudanças de agendamento
    const interval = setInterval(filterMedias, 30000);

    return () => clearInterval(interval);
  }, [medias, selectedScreen, userId]);

  // Gera hash das mídias filtradas para detectar mudanças
  const filteredMediasHash = useMemo(() => {
    return filteredMedias
      .map(m => `${m.id}`)
      .sort()
      .join('|');
  }, [filteredMedias]);

  // Lista de IDs das mídias filtradas
  const filteredMediaIds = useMemo(() => {
    return filteredMedias.map(m => m.id);
  }, [filteredMedias]);

  return {
    filteredMedias,
    filteredMediasHash,
    filteredMediaIds
  };
}
